// Formatting helpers for the Billing tab.
//
// DB timestamps are UTC (see the server entities). We render dates in UTC on
// purpose so an invoice's "date" can't drift across a day boundary depending on
// the viewer's timezone — `TIMEZONE_NOTE` is surfaced in the UI to make that
// explicit. Money is rendered as "<CURRENCY> <amount>" (e.g. "INR 299.00") to
// match the existing Plans/Settings copy rather than a locale currency symbol.

export const TIMEZONE_NOTE = 'Dates shown in UTC';

const DATE_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

const DATETIME_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'UTC',
  timeZoneName: 'short',
});

/** Parse an ISO string / Date into a valid Date, or null. */
function parse(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Jun 14, 2026" (UTC). Returns `fallback` for missing/invalid input. */
export function formatDate(value: string | Date | null | undefined, fallback = '—'): string {
  const d = parse(value);
  return d ? DATE_FMT.format(d) : fallback;
}

/** "Jun 14, 2026, 3:04 PM UTC" — used in the invoice detail modal. */
export function formatDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  const d = parse(value);
  return d ? DATETIME_FMT.format(d) : fallback;
}

/** A new Date `days` after `value`, or null. Does not mutate the input. */
export function addDays(value: string | Date | null | undefined, days: number): Date | null {
  const d = parse(value);
  if (!d) return null;
  const out = new Date(d);
  out.setUTCDate(out.getUTCDate() + days);
  return out;
}

/**
 * Next renewal date for display. Per the billing spec it is the day AFTER the
 * current period ends (billingPeriodEnd + 1 day). Returns `fallback` when the
 * period end is unknown.
 */
export function renewalDate(billingPeriodEnd: string | Date | null | undefined, fallback = '—'): string {
  const next = addDays(billingPeriodEnd, 1);
  return next ? formatDate(next) : fallback;
}

/** "INR 1,299.00". `amount` already a number; currency defaults to INR. */
export function formatMoney(amount: number | null | undefined, currency = 'INR'): string {
  const n = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
  const formatted = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${currency} ${formatted}`;
}

/** Compact integer with thousands separators, e.g. 20000000 → "20,000,000". */
export function formatNumber(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '0';
  return n.toLocaleString('en-US');
}
